import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { reportingGoals } from "../../src/reportingGoals";

library.add(fas);

const formatNumber = (value, options = {}) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return "0";
  }

  return numericValue.toLocaleString(undefined, options);
};

const formatCurrency = (value, currency) => {
  return `${currency || ""}${formatNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const formatPercent = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return "0%";
  }

  return `${numericValue.toFixed(2)}%`;
};

const formatAnalysisPeriod = (analysisPeriod) => {
  if (!analysisPeriod?.start || !analysisPeriod?.end) {
    return null;
  }

  const start = new Date(analysisPeriod.start);
  const end = new Date(analysisPeriod.end);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const formatter = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return `${formatter.format(start)} – ${formatter.format(end)}`;
};

const MetricCard = ({ label, value }) => (
  <div className="border-solid border-[1px] border-slate-200 rounded-xl p-4 mb-3 bg-white">
    <div className="row hard-center">
      <span className="field-title">{label}</span>
    </div>
    <h4 className="report-count bold" style={{ margin: "5px 0px 0px" }}>
      {value}
    </h4>
  </div>
);

const OptimizationIdeaCard = ({ idea, index, isPlaceholder }) => (
  <div
    className={`col-md-6 col-sm-12 mb-4 ${isPlaceholder ? "opacity-70" : ""}`}
  >
    <div className="border-solid border-[1px] border-slate-200 rounded-xl overflow-hidden h-full bg-white">
      <div className="bg-blue-600 text-white px-4 py-2 semibold">
        {index + 1}
      </div>
      <div className="p-4">
        <p className="semibold text-xl text-black mb-2">{idea.title}</p>
        <p className="light text-xl mb-0">{idea.description}</p>
      </div>
    </div>
  </div>
);

const TopCampaignSlide = ({ slide, currency, goals }) => {
  const metrics = slide.metrics || {};

  return (
    <div className="border-solid border-[1px] border-slate-200 rounded-xl shadow-sm bg-white mb-8 overflow-hidden">
      <div
        className="row hard-center"
        style={{
          padding: "13px 15px 15px",
          borderBottom: "1px solid rgb(233, 235, 241)",
        }}
      >
        <div className="col-xs-12">
          {slide.badge && (
            <span className="inline-flex items-center px-2 py-1 mb-2 rounded text-base font-medium bg-blue-100 text-blue-700 uppercase">
              <FontAwesomeIcon icon="link" className="mr-2" />
              {slide.badge}
            </span>
          )}
          <h4 className="bold mb-0">
            <a
              href={slide.campaign_url}
              className="text-black hover:text-blue-600"
              target="_blank"
              rel="noopener noreferrer"
            >
              {slide.cta_type_label ? `${slide.cta_type_label} — ` : ""}
              {slide.name}
            </a>
          </h4>
        </div>
      </div>

      <div className="px-[15px] py-4">
        <div className="row hard-center">
          <div className="col-md-6 col-sm-12 mb-4">
            <a
              href={slide.campaign_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div
                className="campaign-template-item-image border-solid border-slate-200 border-[1px] rounded-xl"
                style={{
                  backgroundImage: slide.screenshot_url
                    ? `url(${slide.screenshot_url})`
                    : "none",
                  backgroundColor: slide.screenshot_url ? undefined : "#f8fafc",
                  minHeight: "280px",
                  backgroundSize: "cover",
                  backgroundPosition: "top center",
                }}
              >
                {!slide.screenshot_url && (
                  <div className="flex items-center justify-center h-full light text-xl p-8 text-center">
                    Campaign preview unavailable
                  </div>
                )}
              </div>
            </a>
          </div>

          <div className="col-md-6 col-sm-12">
            {goals.checkouts && (
              <MetricCard
                label="Revenue"
                value={formatCurrency(metrics.revenue, currency)}
              />
            )}
            {goals.checkouts && (
              <MetricCard
                label="AOV"
                value={formatCurrency(metrics.aov, currency)}
              />
            )}
            {goals.bookings && (
              <MetricCard
                label="Bookings"
                value={formatNumber(metrics.bookings)}
              />
            )}
            {goals.custom && (
              <MetricCard
                label={goals.customGoalLabel}
                value={formatNumber(metrics.custom_goals)}
              />
            )}
            <MetricCard
              label="Conversion Rate"
              value={formatPercent(metrics.conversion_rate)}
            />
            <MetricCard
              label="Unique Views"
              value={formatNumber(metrics.views)}
            />
          </div>
        </div>

        <div className="mt-4">
          <h4 className="bold mb-3">Overview</h4>
          <div
            className={`rounded-xl p-4 border-solid border-[1px] ${
              slide.callout_is_placeholder
                ? "border-dashed border-slate-300 bg-slate-50"
                : "border-slate-200 bg-white"
            }`}
          >
            <p
              className={`text-xl mb-0 ${
                slide.callout_is_placeholder ? "light" : ""
              }`}
            >
              {slide.callout}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <h4 className="bold mb-3">Optimization Ideas</h4>
          <div className="row hard-center">
            {(slide.optimization_ideas || []).slice(0, 3).map((idea, index) => (
              <OptimizationIdeaCard
                key={`${slide.id}-idea-${index}`}
                idea={idea}
                index={index}
                isPlaceholder={slide.optimization_ideas_are_placeholder}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const WebsiteTopCampaignInsights = (props) => {
  const { slides, currency, analysisPeriod, website } = props;
  const goals = reportingGoals(website);

  if (!slides || slides.length === 0) {
    return null;
  }

  const periodLabel = formatAnalysisPeriod(analysisPeriod);

  return (
    <div className="WebsiteTopCampaignInsights some-vertical-space">
      <div
        className="row hard-center"
        style={{
          padding: "13px 15px 0px",
          borderBottom: "1px solid rgb(233, 235, 241)",
        }}
      >
        <div className="col-xs-12" style={{ marginBottom: "10px" }}>
          <h4 className="bold">Top Campaign Highlights</h4>
          {periodLabel && (
            <p className="light text-xl mb-0">Performance for {periodLabel}</p>
          )}
        </div>
      </div>

      <div className="px-[15px] pt-4">
        {slides.map((slide) => (
          <TopCampaignSlide
            key={`${slide.id}-${slide.badge || "campaign"}`}
            slide={slide}
            currency={currency}
            goals={goals}
          />
        ))}
      </div>
    </div>
  );
};

export default WebsiteTopCampaignInsights;
